import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  fetchAgreementTemplate,
  fetchApplication,
  fetchMarkets,
  formatMarketDateOption,
  updateMyApplication,
  type Application,
  type Market,
  type VendorCategoryOption,
} from '@/api/applicationsApi'
import { ApiClientError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { MarkdownDocument } from '@/components/forms/MarkdownDocument'
import { SignaturePad } from '@/components/forms/SignaturePad'

import authStyles from './AuthPages.module.css'
import styles from './PortalPages.module.css'

type VendorCategory = 'commercial' | 'school_community' | 'learner_classroom'

type EditForm = {
  market_id: number
  applicant_name: string
  business_name: string
  email: string
  cellphone: string
  identity_number: string
  items_for_sale: string
  vendor_category: VendorCategory
  supervising_teacher: string
  electricity_needed: boolean
  agreement_accepted: boolean
  signatory_name: string
  signed_on: string
  signature_data: string
}

function toForm(app: Application): EditForm {
  return {
    market_id: app.market_id,
    applicant_name: app.applicant_name,
    business_name: app.business_name,
    email: app.email,
    cellphone: app.cellphone,
    identity_number: app.identity_number || '',
    items_for_sale: app.items_for_sale || '',
    vendor_category: (app.vendor_category || 'commercial') as VendorCategory,
    supervising_teacher: app.supervising_teacher || '',
    electricity_needed: Boolean(app.electricity_needed),
    agreement_accepted: Boolean(app.agreement_accepted),
    signatory_name: app.signatory_name || '',
    signed_on: (app.signed_on || new Date().toISOString().slice(0, 10)).slice(0, 10),
    signature_data: app.signature_data || '',
  }
}

/** Vendor edit for a submitted/rejected application (locked once approved). */
export function VendorApplicationEditPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isAdmin } = useAuth()
  const [application, setApplication] = useState<Application | null>(null)
  const [markets, setMarkets] = useState<Market[]>([])
  const [form, setForm] = useState<EditForm | null>(null)
  const [agreementMarkdown, setAgreementMarkdown] = useState('')
  const [categories, setCategories] = useState<VendorCategoryOption[]>([])
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated || isAdmin || !id) return
    void (async () => {
      setLoading(true)
      setError('')
      try {
        const [appResult, marketResult, agreement] = await Promise.all([
          fetchApplication(Number(id)),
          fetchMarkets(),
          fetchAgreementTemplate(),
        ])
        setApplication(appResult.application)
        setForm(toForm(appResult.application))
        setMarkets(marketResult.markets)
        setAgreementMarkdown(agreement.markdown)
        setCategories(agreement.vendor_categories)
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load application.')
      } finally {
        setLoading(false)
      }
    })()
  }, [id, isAuthenticated, isAdmin])

  const selected = useMemo(() => {
    if (!form) return null
    return markets.find((market) => market.id === form.market_id) ?? null
  }, [form, markets])

  const marketOptions = useMemo(() => {
    if (!application) return markets
    if (markets.some((market) => market.id === application.market_id)) return markets
    return [
      {
        id: application.market_id,
        title: application.market_title || `Market #${application.market_id}`,
        event_date: application.market_event_date || null,
        fee_standard: 100,
        fee_with_electricity: 150,
      } satisfies Market,
      ...markets,
    ]
  }, [application, markets])

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (isAdmin) return <Navigate to="/admin" replace />

  const locked = application?.status === 'approved'

  function updateField<K extends keyof EditForm>(key: K, value: EditForm[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current))
    setSaved('')
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!application || !form || locked) return
    if (form.vendor_category === 'learner_classroom' && !form.supervising_teacher.trim()) {
      setError('Supervising teacher is required for learner / classroom markets.')
      return
    }
    if (!form.signature_data) {
      setError('Please sign the form.')
      return
    }
    setBusy(true)
    setError('')
    setSaved('')
    try {
      const result = await updateMyApplication(application.id, {
        market_id: form.market_id,
        applicant_name: form.applicant_name,
        business_name: form.business_name,
        email: form.email,
        cellphone: form.cellphone,
        identity_number: form.identity_number || null,
        items_for_sale: form.items_for_sale,
        vendor_category: form.vendor_category,
        supervising_teacher:
          form.vendor_category === 'learner_classroom' ? form.supervising_teacher : null,
        electricity_needed: form.electricity_needed,
        agreement_accepted: form.agreement_accepted,
        signatory_name: form.signatory_name,
        signed_on: form.signed_on,
        signature_data: form.signature_data,
      })
      setApplication(result.application)
      setForm(toForm(result.application))
      setSaved('Application updated.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save application.')
    } finally {
      setBusy(false)
    }
  }

  const fee = selected
    ? form?.electricity_needed
      ? selected.fee_with_electricity
      : selected.fee_standard
    : form?.electricity_needed
      ? 150
      : 100

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Edit application</h1>
          <p className={styles.lead}>
            {locked
              ? 'This stall is approved and locked. Ask an admin if you need a change.'
              : 'You can update your details until the stall is approved.'}
          </p>
        </div>
        <Link className={styles.ghost} to="/vendor">
          Back
        </Link>
      </header>

      {loading ? <p className={styles.empty}>Loading application…</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}
      {saved ? <p className={styles.metaOk}>{saved}</p> : null}

      {application && form ? (
        <form className={styles.formGrid} onSubmit={(event) => void onSubmit(event)}>
          <label className={authStyles.label}>
            Market date
            <select
              className={authStyles.select}
              value={form.market_id}
              onChange={(e) => updateField('market_id', Number(e.target.value))}
              required
              disabled={locked}
            >
              {marketOptions.map((market) => (
                <option key={market.id} value={market.id}>
                  {formatMarketDateOption(market)}
                </option>
              ))}
            </select>
          </label>

          <p className={styles.meta}>
            Status:{' '}
            <strong className={locked ? styles.metaOk : undefined}>{application.status}</strong>
          </p>

          <label className={authStyles.label}>
            Vendor category
            <select
              className={authStyles.select}
              value={form.vendor_category}
              onChange={(e) => updateField('vendor_category', e.target.value as VendorCategory)}
              required
              disabled={locked}
            >
              {(categories.length
                ? categories
                : [
                    { value: 'commercial' as const, label: 'Commercial Vendor' },
                    { value: 'school_community' as const, label: 'School Community Vendor' },
                    { value: 'learner_classroom' as const, label: 'Learner / Classroom Market' },
                  ]
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {form.vendor_category === 'learner_classroom' ? (
            <label className={authStyles.label}>
              Supervising teacher
              <input
                className={authStyles.input}
                value={form.supervising_teacher}
                onChange={(e) => updateField('supervising_teacher', e.target.value)}
                required
                disabled={locked}
              />
            </label>
          ) : null}

          <label className={authStyles.label}>
            Name
            <input
              className={authStyles.input}
              value={form.applicant_name}
              onChange={(e) => updateField('applicant_name', e.target.value)}
              required
              disabled={locked}
            />
          </label>
          <label className={authStyles.label}>
            Business
            <input
              className={authStyles.input}
              value={form.business_name}
              onChange={(e) => updateField('business_name', e.target.value)}
              required
              disabled={locked}
            />
          </label>
          <label className={authStyles.label}>
            Identity / registration number (optional)
            <input
              className={authStyles.input}
              value={form.identity_number}
              onChange={(e) => updateField('identity_number', e.target.value)}
              disabled={locked}
            />
          </label>
          <label className={authStyles.label}>
            Email
            <input
              className={authStyles.input}
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
              disabled={locked}
            />
          </label>
          <label className={authStyles.label}>
            Cellphone Number
            <input
              className={authStyles.input}
              value={form.cellphone}
              onChange={(e) => updateField('cellphone', e.target.value)}
              required
              disabled={locked}
            />
          </label>
          <label className={authStyles.label}>
            Please list items for sale
            <textarea
              className={authStyles.textarea}
              value={form.items_for_sale}
              onChange={(e) => updateField('items_for_sale', e.target.value)}
              required
              disabled={locked}
            />
          </label>

          <label className={styles.check}>
            <input
              type="checkbox"
              checked={form.electricity_needed}
              onChange={(e) => updateField('electricity_needed', e.target.checked)}
              disabled={locked}
            />
            Electricity needed (fee R{selected?.fee_with_electricity ?? 150} instead of R
            {selected?.fee_standard ?? 100})
          </label>

          <div className={styles.notice}>
            <p>
              Fee of <strong>R{fee.toFixed(2)}</strong> to be paid on or before{' '}
              {selected?.application_deadline ?? 'deadline'} into {selected?.bank_name ?? 'FNB'}{' '}
              account <strong>{selected?.bank_account ?? '—'}</strong>.
            </p>
          </div>

          <div className={styles.agreement}>
            <h2>Vendor application, terms & conditions, and indemnity</h2>
            <div className={styles.agreementScroll}>
              {agreementMarkdown ? (
                <MarkdownDocument markdown={agreementMarkdown} />
              ) : (
                <p>Loading agreement…</p>
              )}
            </div>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={form.agreement_accepted}
                onChange={(e) => updateField('agreement_accepted', e.target.checked)}
                required
                disabled={locked}
              />
              I declare that I have read this Agreement in its entirety and agree to comply with all
              its terms and conditions
            </label>
          </div>

          <label className={authStyles.label}>
            Name and Surname
            <input
              className={authStyles.input}
              value={form.signatory_name}
              onChange={(e) => updateField('signatory_name', e.target.value)}
              required
              disabled={locked}
            />
          </label>
          <label className={authStyles.label}>
            Date
            <input
              className={authStyles.input}
              type="date"
              value={form.signed_on}
              onChange={(e) => updateField('signed_on', e.target.value)}
              required
              disabled={locked}
            />
          </label>
          <div>
            <p className={styles.signLabel}>Signature</p>
            {locked ? (
              application.signature_data ? (
                <img
                  src={application.signature_data}
                  alt="Saved signature"
                  style={{ maxWidth: '100%', background: '#fff', borderRadius: '0.75rem' }}
                />
              ) : (
                <p className={styles.empty}>No signature on file.</p>
              )
            ) : (
              <SignaturePad
                key={application.id}
                initialValue={application.signature_data}
                onChange={(value) => updateField('signature_data', value)}
              />
            )}
          </div>

          {!locked ? (
            <button className={authStyles.submit} type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          ) : (
            <button type="button" className={styles.secondary} onClick={() => navigate('/vendor')}>
              Back to vendor area
            </button>
          )}
        </form>
      ) : null}
    </section>
  )
}
